import React, { useState, useEffect } from 'react'
import { ChevronLeft, X, Search } from 'lucide-react'
import { catalogAPI } from '../../services/api'
import { injectFontFace, injectFontFamilyVariants } from '../../utils/fontLoader'

/**
 * Fonts Secondary Sidebar Component
 * Displays fonts with preview text to help identify how text will look
 */
const FontsSecondarySidebar = ({ 
  isOpen, 
  onClose, 
  onFontSelect,
  previewText = 'Hello World' // Default preview text
}) => {
  const [fonts, setFonts] = useState([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  // System fonts removed - only using fonts from CDN now

  // Store all fonts for variant injection
  const [allFonts, setAllFonts] = useState([])
  
  // Track which fonts are available/loaded
  const [fontAvailability, setFontAvailability] = useState(new Map())

  // Load fonts from API (fonts_list.json)
  useEffect(() => {
    if (!isOpen) return

    const loadFonts = async () => {
      try {
        setLoading(true)
        const response = await catalogAPI.getFonts()
        if (response.data?.success && response.data.data?.fonts) {
          // Convert CDN URLs to proxy URLs for all fonts
          const fontsList = response.data.data.fonts.map(font => {
            const filename = font.file || font.filename
            // Convert CDN URL to proxy URL to avoid CORS issues
            let url = font.url
            if (url && url.startsWith('https://editor.blr1.cdn.digitaloceanspaces.com/fonts/')) {
              url = `/api/v1/catalog/fonts/proxy/${encodeURIComponent(filename)}`
            } else if (filename && !url) {
              url = `/api/v1/catalog/fonts/proxy/${encodeURIComponent(filename)}`
            }
            return { ...font, url }
          })
          
          // Store all fonts for variant injection
          setAllFonts(fontsList)
          
          // Extract unique font families
          const familyMap = new Map()
          fontsList.forEach(font => {
            if (font.family && !familyMap.has(font.family)) {
              familyMap.set(font.family, font)
            }
          })
          
          // Convert to sorted array
          const uniqueFonts = Array.from(familyMap.values()).sort((a, b) => 
            a.family.localeCompare(b.family)
          )
          
          setFonts(uniqueFonts)
          
          // Load fonts dynamically - inject ALL fonts (including N to Z)
          // This ensures all fonts are available when scrolling
          uniqueFonts.forEach(font => {
            try {
              // Inject all variants (weights/styles) for this font family
              injectFontFamilyVariants(fontsList, font.family, 20)
            } catch (err) {
              console.warn(`Failed to inject font variants for ${font.family}:`, err)
            }
          })
          
          // Check font availability after a delay to allow fonts to load
          setTimeout(() => {
            checkFontAvailability(uniqueFonts)
          }, 1000)
        }
      } catch (error) {
        console.error('Failed to load fonts from API:', error)
      } finally {
        setLoading(false)
      }
    }

    loadFonts()
  }, [isOpen])

  // Check if a font is available/loaded using canvas measurement
  const checkFontAvailability = async (fontsToCheck) => {
    const availabilityMap = new Map()
    
    // Wait for fonts to load
    if (document.fonts && document.fonts.ready) {
      try {
        await document.fonts.ready
      } catch (err) {
        console.warn('Font loading check failed:', err)
      }
    }
    
    // Check API fonts using canvas measurement
    for (const font of fontsToCheck) {
      const fontFamily = font.family
      if (!fontFamily) continue
      
      try {
        let isAvailable = false
        
        // Method 1: Use Font Loading API if available
        if (document.fonts && document.fonts.check) {
          isAvailable = document.fonts.check(`12px "${fontFamily}"`)
        } else {
          // Method 2: Use canvas to measure if font renders differently
          const canvas = document.createElement('canvas')
          const context = canvas.getContext('2d')
          
          // Measure text with fallback font
          context.font = '12px monospace'
          const fallbackWidth = context.measureText('mmmmmmmmmm').width
          
          // Measure text with target font
          context.font = `12px "${fontFamily}", monospace`
          const fontWidth = context.measureText('mmmmmmmmmm').width
          
          // If widths are different, font is loaded
          isAvailable = Math.abs(fontWidth - fallbackWidth) > 0.1
        }
        
        availabilityMap.set(fontFamily, isAvailable)
      } catch (err) {
        // If check fails, assume font is not available
        availabilityMap.set(fontFamily, false)
      }
    }
    
    setFontAvailability(availabilityMap)
  }
  
  // Get preview text (first 20 characters)
  const getPreviewText = (text) => {
    if (!text) return 'AaBbCc 123'
    return text.substring(0, 20) || 'AaBbCc 123'
  }

  const previewTextToShow = getPreviewText(previewText)
  
  // Check if font is available
  const isFontAvailable = (fontName) => {
    // Check the availability map for CDN fonts
    return fontAvailability.get(fontName) !== false
  }

  // Get unique font families from API fonts
  const apiFontFamilies = fonts.map(f => f.family).filter(Boolean)
  
  // Filter fonts based on search term
  const filteredApiFonts = apiFontFamilies.filter(font =>
    font.toLowerCase().includes(searchTerm.toLowerCase())
  )

  // Get font style for rendering
  const getFontStyle = (fontName) => {
    // All fonts are from CDN, use the font name directly
    return { fontFamily: `"${fontName}", sans-serif` }
  }

  const handleFontSelect = (font) => {
    if (onFontSelect) {
      onFontSelect(font)
    }
    onClose()
  }

  if (!isOpen) return null

  return (
    <div className="fixed top-0 right-0 h-full w-80 bg-white border-l border-gray-200 shadow-xl z-[1000] flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50">
        <div className="flex items-center space-x-2">
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-200 rounded transition-colors"
            title="Close"
          >
            <ChevronLeft className="w-5 h-5 text-gray-700" />
          </button>
          <h2 className="text-lg font-semibold text-gray-800">Fonts</h2>
        </div>
        <button
          onClick={onClose}
          className="p-1 hover:bg-gray-200 rounded transition-colors"
          title="Close"
        >
          <X className="w-5 h-5 text-gray-700" />
        </button>
      </div>

      {/* Search */}
      <div className="p-4 border-b border-gray-200">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search fonts..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            autoFocus
          />
        </div>
        {/* Preview text display */}
        <div className="mt-3 text-xs text-gray-600">
          <span className="font-medium">Preview:</span> {previewTextToShow}
        </div>
      </div>

      {/* Font List - Scrollable */}
      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-2 text-sm text-gray-600">Loading fonts...</p>
            </div>
          </div>
        ) : (
          <div className="p-2">
            {/* Available Fonts Section (from fonts_list.json) */}
            {filteredApiFonts.length > 0 ? (
              <div className="mb-4">
                <div className="sticky top-0 bg-green-100 px-3 py-2 text-xs font-semibold text-green-800 border-b border-green-200 mb-2 z-10">
                  Available Fonts ({filteredApiFonts.length})
                </div>
                {filteredApiFonts.map((font) => {
                  const available = isFontAvailable(font)
                  return (
                    <button
                      key={font}
                      type="button"
                      onClick={() => handleFontSelect(font)}
                      className="w-full px-3 py-3 text-left hover:bg-green-50 focus:outline-none focus:bg-green-50 border-b border-gray-100 transition-colors group"
                    >
                      <div className="font-semibold text-xs text-gray-600 mb-1">{font}</div>
                      <div
                        className={`text-base truncate ${available ? 'text-gray-800' : 'text-red-500 italic'}`}
                        style={available ? getFontStyle(font) : {}}
                      >
                        {available ? previewTextToShow : 'Not Available'}
                      </div>
                    </button>
                  )
                })}
              </div>
            ) : (
              <div className="px-3 py-8 text-sm text-gray-500 text-center">
                No fonts found matching "{searchTerm}"
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export default FontsSecondarySidebar

